import { Fragment, useCallback, useEffect, useState } from 'react';
import NewOrderForm from '../components/NewOrderForm.tsx';
import { MakeTag, resetJobMakes } from '../lib/jobMakes.tsx';
import { BOX_TYPES, faceChoosable, makeLabel, type Make } from '../lib/boxTypes.ts';
import { api, fmt, fmtDate } from '../api.ts';
import type { User } from '../api.ts';

/** One line of a job's timing table: a wait before a stage, or the stage. */
interface Step {
  position: number;
  kind: 'WAIT' | 'WORK';
  label: string;
  from: string;
  to: string | null;
}
interface Job {
  id: number; jobNumber: string; status: string;
  orderedQuantity: string; productionQuantity: string;
  cardMadeAt: string | null; createdAt: string;
  place: string; deliveryDate: string | null; ownDeliveryDate: boolean;
  timeline: Step[];
}
/** One board issued to a job being cancelled: sheets is what the user typed. */
type BoardReturn = { itemId: number; name: string; issued: number; sheets: string };

/** Whole sheets, from none up to what was issued. */
function badReturn(r: BoardReturn): boolean {
  const n = Number(r.sheets);
  return r.sheets === '' || !Number.isInteger(n) || n < 0 || n > r.issued;
}

/** A job's board from Board stock (053): what its card needs and where it stands. */
interface Board {
  jobNumber: string; board: string; sheets: number; paired: number; short: number;
  status: 'PAIRED' | 'AVAILABLE' | 'SHARED' | 'SHORT' | 'ISSUED'; issued: number;
}
interface Item {
  id: number; lineNo: number; productName: string; specification: string | null;
  boxSize: string | null; boxType: string | null; make: string | null; bufferOfName: string | null; ups: number | null;
  deliveryDate: string | null;
  boardRequired: boolean;
  /*
   * Numbers, not strings. getPurchaseOrder builds the lines with
   * json_build_object, and Postgres renders a NUMERIC column into JSON as a
   * number -- unlike a plain column read, which node-postgres hands back as a
   * string. These were declared as strings, which type-checked happily and
   * then threw "(form.unitPrice ?? '').trim is not a function" the first time
   * somebody saved the correction form.
   */
  quantity: number; unitPrice: number | null;
  notes: string | null; released: number; unreleased: number; uncarded: number;
  lineStatus: LineStatus; deliveryState: string;
  jobs: Job[]; boards: Board[];
}
type LineStatus = 'OPEN' | 'PART_DISPATCHED' | 'DISPATCHED' | 'CANCELLED';
const lineRef = (so: number, line: number) => `SO${so}-${line}`;

const STATUS_LABEL: Record<LineStatus, string> = {
  OPEN: 'Open', PART_DISPATCHED: 'Part dispatched', DISPATCHED: 'Dispatched', CANCELLED: 'Cancelled',
};
type Tab = 'LINES' | 'BOARD' | 'TIMINGS' | 'HISTORY';
type MenuAt = { right: number; top?: number; bottom?: number };
const menuAt = (el: HTMLElement): MenuAt => {
  const r = el.getBoundingClientRect();
  const right = window.innerWidth - r.right;
  return window.innerHeight - r.bottom < 120
    ? { right, bottom: window.innerHeight - r.top + 6 }
    : { right, top: r.bottom + 6 };
};

interface Order {
  id: number; po_number: string | null; so_number: number; po_date: string;
  priority: string; status: string; notes: string | null; paid: string;
  customer_id: number; customer_name: string;
  customer_phone: string | null; customer_address: string | null;
  items: Item[];
}
interface ImpactJob {
  job_number: string; status: string; ordered_quantity: string;
  current_stage: string | null; dispatched_quantity: string;
  pending_dispatch: string;
}
interface Event {
  audit_id: number; created_at: string; action: string;
  user_name: string; kind: string; label: string | null;
  changed_fields: string[];
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
}

function boardTag(b: Board) {
  switch (b.status) {
    case 'PAIRED': return <span className="tag ready">Paired</span>;
    case 'AVAILABLE': return <span className="tag ready">Available</span>;
    case 'SHARED': return <span className="tag waiting">Available · shared</span>;
    case 'SHORT': return <span className="tag waiting">Short {fmt(b.short)}</span>;
    default: return <span className="tag blocked">Issued {fmt(String(b.issued))}</span>;
  }
}

/** Fields nobody wants narrated: bookkeeping, not decisions. */
const NOISE = new Set(['updated_at', 'created_at', 'id']);

const val = (v: unknown) =>
  v === null || v === undefined || v === '' ? '—' : String(v);

/**
 * One line of history, in words.
 *
 * Built from the field changes rather than from a hand-written sentence per
 * event type: there are dozens of those and they drift out of date the moment
 * a column is added. "status: DRAFT → CREATED" is plainer than a sentence and
 * cannot go stale.
 */
function describe(e: Event): string {
  if (e.action === 'INSERT') return 'created';
  if (e.action === 'DELETE') return 'deleted';
  const fields = e.changed_fields.filter((f) => !NOISE.has(f));
  if (fields.length === 0) return 'changed';
  return fields
    .slice(0, 4)
    .map((f) => `${f.replace(/_/g, ' ')}: ${val(e.old_values?.[f])} → ${val(e.new_values?.[f])}`)
    .join(' · ') + (fields.length > 4 ? ` · +${fields.length - 4} more` : '');
}

export default function OrderDetail({
  poId, focusItemId, onBack, onOpenCard, onOpenJob,
}: {
  poId: number; focusItemId?: number | null; onBack: () => void;
  onOpenCard: (poItemId: number) => void; onOpenJob: (jobId: number) => void;
}) {
  const [order, setOrder] = useState<Order | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [error, setError] = useState('');
  /*
   * Read on mount rather than taken as a prop, which is how Settings.tsx and
   * Roles.tsx get it. Deliberate and temporary: passing it down means editing
   * App.tsx, which another piece of work is in the middle of. Worth
   * normalising to the prop once that lands -- hiding a button is a courtesy
   * either way, since the route checks the permission for itself.
   */
  const [canCancel, setCanCancel] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  /** Which form is open: the order header, or one line by id. */
  const [editing, setEditing] = useState<'order' | number | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [editError, setEditError] = useState('');
  /** What the jobs on this line have already done, read when the form opens. */
  const [impact, setImpact] = useState<ImpactJob[]>([]);
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  /** Only asked when the box name actually changes -- see the service. */
  const [renameMode, setRenameMode] = useState<'typo' | 'different'>('typo');
  const [originalName, setOriginalName] = useState('');
  /** Only asked when a RELEASED job cannot silently follow the line. */
  const [quantityMode, setQuantityMode] = useState<'typo' | 'additional'>('typo');
  const [originalQty, setOriginalQty] = useState('');
  /*
   * The server asked for something this form did not offer. It should never
   * happen -- the same conditions decide both -- but when a tab has been open
   * across a deploy it does, and the result is a form that refuses to save
   * and shows no way to fix it. Forcing the choice open means the service
   * stays the authority on what is required.
   */
  const [forcePrompt, setForcePrompt] = useState<'rename' | 'quantity' | null>(null);
  const [cancelling, setCancelling] = useState<Job | null>(null);
  const [reason, setReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  /** Board issued to the job being cancelled, and how much is coming back. */
  const [returns, setReturns] = useState<BoardReturn[]>([]);
  const [returnsNote, setReturnsNote] = useState('');
  const [onOrder, setOnOrder] = useState<{ name: string; sheets: number; from: string | null; due: string | null }[]>([]);
  const [notice, setNotice] = useState('');
  const [addingLine, setAddingLine] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuFor, setMenuFor] = useState<({ job: Job } & MenuAt) | null>(null);
  const [lineMenuFor, setLineMenuFor] = useState<({ item: Item } & MenuAt) | null>(null);
  const [showCancelled, setShowCancelled] = useState<Set<number>>(new Set());
  const [closed, setClosed] = useState<Set<number>>(new Set());
  const [focused, setFocused] = useState<number | null>(null);
  const [tab, setTab] = useState<Tab>('LINES');
  const [timingJob, setTimingJob] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [o, t] = await Promise.all([
        api.get<{ purchaseOrder: Order }>(`/api/purchase-orders/${poId}`),
        api.get<{ timeline: Event[] }>(`/api/purchase-orders/${poId}/timeline`),
      ]);
      setOrder(o.purchaseOrder);
      setEvents(t.timeline);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the order');
    }
  }, [poId]);

  useEffect(() => { void load(); }, [load]);

  const firstLine = order?.items[0]?.id ?? null;
  useEffect(() => {
    if (firstLine === null || !focusItemId || focusItemId === firstLine) return;
    const el = document.getElementById(`line-${focusItemId}`);
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    setFocused(focusItemId);
    const t = setTimeout(() => setFocused(null), 1600);
    return () => clearTimeout(t);
  }, [firstLine, focusItemId]);

  useEffect(() => {
    api.get<{ user: User }>('/api/auth/me')
      .then((r) => {
        setCanCancel(r.user.permissions?.includes('prepress.cancel_job') ?? false);
        setCanEdit(r.user.permissions?.includes('orders.update') ?? false);
      })
      .catch(() => { setCanCancel(false); setCanEdit(false); });
  }, []);

  function openOrderEdit() {
    if (!order) return;
    setEditError('');
    setImpact([]);
    api.get<{ customers: { id: number; name: string }[] }>('/api/customers?pageSize=200')
      .then((r) => setCustomers(r.customers))
      .catch(() => setCustomers([]));
    setForm({
      customerId: String(order.customer_id),
      poNumber: order.po_number ?? '',
      poDate: order.po_date.slice(0, 10),
      priority: order.priority,
      notes: order.notes ?? '',
    });
    setEditing('order');
  }

  function openLineEdit(it: Item) {
    setEditError('');
    setImpact([]);
    api.get<{ jobs: ImpactJob[] }>(`/api/po-items/${it.id}/impact`)
      .then((r) => setImpact(r.jobs))
      .catch(() => setImpact([]));
    setRenameMode('typo');
    setOriginalName(it.productName);
    setQuantityMode('typo');
    setOriginalQty(String(it.quantity));
    setForcePrompt(null);
    setForm({
      productName: it.productName,
      boxType: it.boxType ?? '',
      make: it.make ?? '',
      layer1: it.boardRequired ? 'BOARD' : 'KRAFT',
      boxSize: it.boxSize ?? '',
      ups: it.ups != null ? String(it.ups) : '',
      quantity: String(it.quantity),
      reason: '',
      deliveryDate: it.deliveryDate?.slice(0, 10) ?? '',
      unitPrice: it.unitPrice != null ? String(it.unitPrice) : '',
      notes: it.notes ?? '',
    });
    setEditing(it.id);
  }

  /*
   * An empty box means "no value", not "leave it alone" -- the office needs to
   * be able to clear a delivery date or a PO number for a phone order. The
   * endpoints take null for that, so empty strings become null rather than
   * being dropped from the patch.
   */
  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());

  async function saveEdit() {
    if (!order || editing === null) return;
    setBusy(true);
    setEditError('');
    try {
      let effects: string[] = [];
      if (editing === 'order') {
        const r = await api.patch<{ purchaseOrder: { effects?: string[] } }>(
          `/api/purchase-orders/${order.id}`, {
          poNumber: orNull(form.poNumber ?? ''),
          poDate: form.poDate,
          priority: form.priority,
          notes: orNull(form.notes ?? ''),
          customerId: Number(form.customerId),
          ...(form.reason?.trim() ? { reason: form.reason.trim() } : {}),
        });
        effects = r.purchaseOrder.effects ?? [];
      } else {
        const price = (form.unitPrice ?? '').trim();
        const ups = (form.ups ?? '').trim();
        const r = await api.patch<{ item: { effects?: string[] } }>(
          `/api/po-items/${editing}`, {
          deliveryDate: orNull(form.deliveryDate ?? ''),
          unitPrice: price === '' ? null : Number(price),
          notes: orNull(form.notes ?? ''),
          productName: (form.productName ?? '').trim(),
          ...((form.productName ?? '').trim().toLowerCase() !== originalName.toLowerCase()
            ? { renameMode } : {}),
          boxType: orNull(form.boxType ?? ''),
          make: (form.make || null) as Make | null,
          ...(faceChoosable(form.make) ? { layer1: form.layer1 === 'KRAFT' ? 'KRAFT' : 'BOARD' } : {}),
          boxSize: orNull(form.boxSize ?? ''),
          ups: ups === '' ? null : Number(ups),
          quantity: Number(form.quantity),
          ...(Number(form.quantity) !== Number(originalQty) ? { quantityMode } : {}),
          ...(form.reason?.trim() ? { reason: form.reason.trim() } : {}),
        });
        effects = r.item.effects ?? [];
        resetJobMakes();
      }
      setEditing(null);
      await load();
      const facts = effects.filter((e) => !e.startsWith('reason:'));
      const saved = editing === 'order' ? `SO${order.so_number}`
        : editItem ? lineRef(order.so_number, editItem.lineNo) : 'Line';
      setNotice([`${saved} saved`, ...facts].join(' · '));
      setImpact([]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save the change';
      setEditError(msg);
      if (/quantity was wrong or the order has changed/i.test(msg)) {
        setForcePrompt('quantity');
      } else if (/typo or a different box/i.test(msg)) {
        setForcePrompt('rename');
      }
    } finally {
      setBusy(false);
    }
  }

  /*
   * A released job has had its board issued. Before production starts it is
   * all still in the store's hands, so all of it is expected back; once the
   * floor has started, only what someone counts back is.
   */
  async function openCancel(j: Job) {
    setCancelling(j); setReason(''); setCancelError(''); setReturns([]); setReturnsNote(''); setOnOrder([]);
    try {
      const b = await api.get<{ productionStarted: boolean; boards: {
        item_id: number; item_name: string; issued: string;
        on_order: string; on_order_from: string | null; on_order_due: string | null;
      }[] }>(`/api/jobs/${j.id}/boards`);
      setOnOrder(b.boards.filter((r) => Number(r.on_order) > 0).map((r) => ({
        name: r.item_name, sheets: Number(r.on_order), from: r.on_order_from, due: r.on_order_due,
      })));
      if (j.status === 'DRAFT') return;
      const issued = b.boards.filter((r) => Number(r.issued) > 0);
      setReturns(issued.map((r) => ({
        itemId: r.item_id, name: r.item_name, issued: Number(r.issued),
        sheets: b.productionStarted ? '0' : String(Number(r.issued)),
      })));
      setReturnsNote(b.productionStarted
        ? 'Production has started — enter what is actually coming back.'
        : 'Production has not started, so all of it is expected back.');
    } catch {
      if (j.status !== 'DRAFT') setCancelError('Could not read the board issued to this job');
    }
  }

  const firstBadReturn = returns.find(badReturn);
  const returnsInvalid = firstBadReturn !== undefined;

  async function confirmCancel() {
    if (!cancelling) return;
    setBusy(true);
    setCancelError('');
    try {
      const res = await api.post<{ cancelled: {
        jobNumber: string; quantityReturned: number; sheetsReturned: number; boardOrderedToStock: number;
      } }>(
        `/api/jobs/${cancelling.id}/cancel`, {
          reason: reason.trim(),
          ...(returns.length
            ? { boardReturns: returns.map((r) => ({ itemId: r.itemId, sheets: Number(r.sheets) })) }
            : {}),
        });
      setCancelling(null);
      setReason('');
      await load();
      const q = res.cancelled.quantityReturned;
      setError('');
      const back = res.cancelled.sheetsReturned
        ? ` · ${fmt(String(res.cancelled.sheetsReturned))} sheets back in stock` : '';
      const ordered = res.cancelled.boardOrderedToStock
        ? ` · ${fmt(String(res.cancelled.boardOrderedToStock))} sheets on order for it will go to stock` : '';
      setNotice(q > 0
        ? `${res.cancelled.jobNumber} cancelled — ${fmt(String(q))} back in pre-press to re-card${back}${ordered}`
        : `${res.cancelled.jobNumber} cancelled${back}${ordered}`);
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Could not cancel the job');
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="panel">
        <div className="panel-head">
          <button className="btn" onClick={onBack}>Back</button>
        </div>
        <div className="error" style={{ margin: 16 }}>{error}</div>
      </div>
    );
  }
  if (!order) return <div className="panel"><div className="empty">Loading…</div></div>;

  const value = order.items.reduce(
    (s, it) => s + Number(it.quantity) * Number(it.unitPrice ?? 0), 0);
  const statuses = order.items.map((it) => it.lineStatus);
  const orderStatus: LineStatus = statuses.every((x) => x === 'CANCELLED') ? 'CANCELLED'
    : statuses.every((x) => x === 'DISPATCHED' || x === 'CANCELLED') ? 'DISPATCHED'
    : statuses.some((x) => x === 'DISPATCHED' || x === 'PART_DISPATCHED') ? 'PART_DISPATCHED' : 'OPEN';
  const boards = order.items.flatMap((it) => it.boards);
  const timed = order.items.flatMap((it) => it.jobs)
    .filter((j) => j.status !== 'CANCELLED' && j.timeline.length > 0);
  const shownTiming = timed.find((j) => j.id === timingJob) ?? timed[0];
  const lineCols = 11;
  const toggleLine = (id: number) => setClosed((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const editItem = typeof editing === 'number' ? order.items.find((x) => x.id === editing) ?? null : null;
  const PRIORITY: Record<string, string> = { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High', URGENT: 'Urgent' };
  const shown = (v: string | null | undefined) => (v === null || v === undefined || v.trim() === '' ? 'blank' : v.trim());
  const moved = (from: string | null | undefined, to: string | null | undefined) =>
    shown(from).toLowerCase() !== shown(to).toLowerCase();
  const edits: { label: string; from: string; to: string; weighty: boolean }[] = [];
  const add = (label: string, from: string | null | undefined, to: string | null | undefined, weighty = false,
               fmtv: (v: string) => string = (v) => v) => {
    if (moved(from, to)) {
      edits.push({ label, from: shown(from) === 'blank' ? 'blank' : fmtv(shown(from)),
                   to: shown(to) === 'blank' ? 'blank' : fmtv(shown(to)), weighty });
    }
  };
  if (editing === 'order') {
    const name = (id: string | undefined) => customers.find((c) => String(c.id) === id)?.name ?? order.customer_name;
    if (form.customerId !== undefined && form.customerId !== String(order.customer_id)) {
      edits.push({ label: 'Company', from: order.customer_name, to: name(form.customerId), weighty: true });
    }
    add('Customer PO', order.po_number, form.poNumber);
    add('Order date', order.po_date.slice(0, 10), form.poDate, false, fmtDate);
    add('Priority', PRIORITY[order.priority], PRIORITY[form.priority ?? 'NORMAL']);
    add('Notes', order.notes, form.notes);
  } else if (editItem) {
    add('Box name', editItem.productName, form.productName, true);
    add('Box type', editItem.boxType, form.boxType, true);
    add('Make', makeLabel(editItem.make) || 'Printed Carton Box', makeLabel(form.make) || 'Printed Carton Box');
    if (faceChoosable(form.make) && form.layer1) add('Layer 1', editItem.boardRequired ? 'Board' : 'Kraft', form.layer1 === 'KRAFT' ? 'Kraft' : 'Board');
    add('Box size', editItem.boxSize, form.boxSize, true);
    add('Ups', editItem.ups != null ? String(editItem.ups) : null, (form.ups ?? '').trim() === '' ? null : String(Number(form.ups)), true);
    if (Number(form.quantity) !== Number(editItem.quantity)) {
      edits.push({ label: 'Quantity', from: fmt(editItem.quantity), to: fmt(form.quantity ?? ''), weighty: true });
    }
    add('Delivery date', editItem.deliveryDate?.slice(0, 10), form.deliveryDate, false, fmtDate);
    const num = (v: string | number | null | undefined) =>
      v === null || v === undefined || String(v).trim() === '' ? null : String(Number(v));
    add('Rate', num(editItem.unitPrice), num(form.unitPrice), false, (v) => `₹${v}`);
    add('Notes', editItem.notes, form.notes);
  }
  const needsReason = edits.some((e) => e.weighty);
  const canSave = edits.length > 0 && (!needsReason || (form.reason ?? '').trim() !== '')
    && !(faceChoosable(form.make) && !form.layer1);

  const lineActions = (it: Item) => {
    const open = it.jobs.filter((j) => j.status !== 'CANCELLED');
    return {
      edit: canEdit && order.status !== 'CANCELLED',
      make: open.length === 0 && it.uncarded > 0 && order.status !== 'CANCELLED',
    };
  };

  return (
    <>
      {notice && (
        <div className="tag ready notice" style={{ marginBottom: 12 }}>
          {notice}
          <button className="btn" style={{ marginLeft: 10, padding: '0 8px', minHeight: 0 }}
                  onClick={() => setNotice('')}>Dismiss</button>
        </div>
      )}
      <div className="panel">
        <div className="order-head">
          <div className="order-title">
            <button className="btn" onClick={onBack}>Back</button>
            <h2>SO{order.so_number}</h2>
            {(order.priority === 'URGENT' || order.priority === 'HIGH') && orderStatus !== 'DISPATCHED' && (
              <span className={`tag ${order.priority === 'URGENT' ? 'urgent' : 'high'}`}>
                {order.priority === 'URGENT' ? 'Urgent' : 'High'}
              </span>
            )}
            <span className={`tag ${orderStatus === 'DISPATCHED' ? 'ready' : 'blocked'}`}>
              {STATUS_LABEL[orderStatus]}
            </span>
            <div className="spacer" />
            {canEdit && order.status !== 'CANCELLED' && (
              <>
                <button className="btn" onClick={() => setAddingLine(true)}>Add line</button>
                <button className="btn" onClick={openOrderEdit}>Edit order</button>
              </>
            )}
          </div>
          <dl className="order-facts">
            <div><dt>Company</dt><dd>{order.customer_name}</dd></div>
            <div><dt>Customer PO</dt><dd className="mono">{order.po_number ?? '—'}</dd></div>
            <div><dt>Ordered</dt><dd>{fmtDate(order.po_date)}</dd></div>
            <div><dt>Value</dt><dd>₹{fmt(value)}</dd></div>
            {Number(order.paid) > 0 && <div><dt>Paid</dt><dd>₹{fmt(order.paid)}</dd></div>}
          </dl>
          {order.notes && <div className="hint" style={{ marginTop: 10 }}>{order.notes}</div>}
        </div>

        <div className="detail-tabs" role="tablist" style={{ padding: '0 16px', margin: 0 }}>
          {([['LINES', 'Lines', order.items.length], ['BOARD', 'Board', boards.length],
             ['TIMINGS', 'Timings', null], ['HISTORY', 'History', events.length]] as [Tab, string, number | null][])
            .map(([key, text, n]) => (
              <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
                {text}{n !== null && <span className="n">{fmt(n)}</span>}
              </button>
            ))}
        </div>

        {tab === 'LINES' && (
          <div className="tbl">
            <table className="fit">
              <thead>
                <tr>
                  <th style={{ width: 34 }} /><th>Line</th><th>Box</th><th>Size</th><th>Type</th>
                  <th className="num">Quantity</th><th className="num">Rate</th><th className="num">Value</th>
                  <th>Delivery</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {order.items.map((it) => {
                  const isOpen = !closed.has(it.id);
                  const cancelled = it.jobs.filter((j) => j.status === 'CANCELLED');
                  const seeCancelled = showCancelled.has(it.id);
                  const jobs = it.jobs.filter((j) => j.status !== 'CANCELLED' || seeCancelled);
                  const acts = lineActions(it);
                  const tint = it.deliveryState === 'LATE' ? 'late' : it.deliveryState === 'DUE_SOON' ? 'soon' : '';
                  return (
                    <Fragment key={it.id}>
                      <tr id={`line-${it.id}`} className={[tint, focused === it.id ? 'row-flash' : ''].join(' ')}
                          style={{ cursor: 'pointer', scrollMarginTop: 16 }} onClick={() => toggleLine(it.id)}>
                        <td>
                          <button className="expander" aria-expanded={isOpen}
                                  aria-label={`${isOpen ? 'Hide' : 'Show'} jobs for ${lineRef(order.so_number, it.lineNo)}`}
                                  onClick={(e) => { e.stopPropagation(); toggleLine(it.id); }}>
                            {isOpen ? '▾' : '▸'}
                          </button>
                        </td>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}>{lineRef(order.so_number, it.lineNo)}</td>
                        <td>
                          {it.productName}
                          {it.bufferOfName && <div className="hint" style={{ marginTop: 2 }}>Buffer for {it.bufferOfName}</div>}
                          {it.specification && <div className="hint" style={{ marginTop: 2 }}>{it.specification}</div>}
                        </td>
                        <td className="dim">
                          {it.boxSize ?? '—'}{it.ups ? ` · ${it.ups} ups` : ''}
                        </td>
                        <td className="dim">{it.boxType ?? '—'}</td>
                        <td className="num">{fmt(it.quantity)}</td>
                        <td className="num dim">{it.unitPrice ? `₹${fmt(it.unitPrice)}` : '—'}</td>
                        <td className="num">₹{fmt(Number(it.quantity) * Number(it.unitPrice ?? 0))}</td>
                        <td className="dim" style={{ whiteSpace: 'nowrap' }}>{fmtDate(it.deliveryDate)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{STATUS_LABEL[it.lineStatus]}</td>
                        <td style={{ textAlign: 'right', width: 1 }}>
                          {(acts.edit || acts.make) && (
                            <button className="btn" aria-haspopup="menu" aria-label={`More actions for ${lineRef(order.so_number, it.lineNo)}`}
                                    onClick={(e) => { e.stopPropagation(); setLineMenuFor({ item: it, ...menuAt(e.currentTarget) }); }}>
                              ⋯
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && jobs.map((j) => {
                        const gone = j.status === 'CANCELLED';
                        const canCancelJob = canCancel && !gone && j.status !== 'DISPATCHED';
                        return (
                          <tr key={j.id} className="sub-row" style={gone ? { opacity: 0.55 } : undefined}>
                            <td /><td />
                            <td style={{ paddingLeft: 24 }}>
                              <button className="linkish mono sub-job" title="Open the job card"
                                      onClick={() => onOpenJob(j.id)}>
                                {j.jobNumber}
                              </button>
                              <MakeTag number={j.jobNumber} />
                            </td>
                            <td /><td />
                            <td className="num">{fmt(j.orderedQuantity)}</td>
                            <td /><td />
                            <td className="dim">{!gone && j.ownDeliveryDate ? `${fmtDate(j.deliveryDate)} · own` : ''}</td>
                            <td className="dim">{j.place}</td>
                            <td style={{ textAlign: 'right', width: 1 }}>
                              {canCancelJob && (
                                <button className="btn" aria-haspopup="menu" aria-label={`More actions for ${j.jobNumber}`}
                                        onClick={(e) => setMenuFor({ job: j, ...menuAt(e.currentTarget) })}>
                                  ⋯
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {isOpen && it.uncarded > 0 && (
                        <tr className="sub-row">
                          <td /><td />
                          <td className="dim" style={{ paddingLeft: 24 }}>No card yet</td>
                          <td /><td />
                          <td className="num">{fmt(it.uncarded)}</td>
                          <td /><td /><td />
                          <td className="dim">Pre-Press</td>
                          <td />
                        </tr>
                      )}
                      {isOpen && it.uncarded < 0 && (
                        <tr className="sub-row">
                          <td /><td />
                          <td colSpan={lineCols - 2} className="dim" style={{ paddingLeft: 24 }}>
                            {fmt(String(Math.abs(it.uncarded)))} more on jobs than the line
                          </td>
                        </tr>
                      )}
                      {isOpen && cancelled.length > 0 && (
                        <tr className="sub-row">
                          <td /><td />
                          <td colSpan={lineCols - 2} style={{ paddingLeft: 24 }}>
                            <button className="linkish" style={{ fontSize: 12.5 }}
                                    onClick={() => setShowCancelled((p) => {
                                      const n = new Set(p);
                                      if (n.has(it.id)) n.delete(it.id); else n.add(it.id);
                                      return n;
                                    })}>
                              {seeCancelled ? 'Hide cancelled' : `Show cancelled (${cancelled.length})`}
                            </button>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              {order.items.length > 1 && (
                <tfoot>
                  <tr>
                    <td colSpan={7} />
                    <td className="num"><strong>₹{fmt(value)}</strong></td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {tab === 'BOARD' && (boards.length === 0 ? (
          <div className="empty">No board on this order.</div>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr><th>Job</th><th>Board</th><th className="num">Sheets</th><th>Stock</th></tr>
              </thead>
              <tbody>
                {boards.map((b) => (
                  <tr key={`${b.jobNumber}:${b.board}`}>
                    <td className="mono">{b.jobNumber}<MakeTag number={b.jobNumber} /></td>
                    <td>{b.board}</td>
                    <td className="num">{fmt(b.sheets)}</td>
                    <td>{boardTag(b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {tab === 'TIMINGS' && (!shownTiming ? (
          <div className="empty">No job has started its route yet.</div>
        ) : (
          <div style={{ padding: '14px 16px 16px' }}>
            {timed.length > 1 && (
              <div className="field" style={{ maxWidth: 220, marginBottom: 4 }}>
                <label htmlFor="timing-job">Job</label>
                <select id="timing-job" value={shownTiming.id} onChange={(e) => setTimingJob(Number(e.target.value))}>
                  {timed.map((j) => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
                </select>
              </div>
            )}
            <JobTiming job={shownTiming} />
          </div>
        ))}

        {tab === 'HISTORY' && (events.length === 0 ? (
          <div className="empty">Nothing recorded against this order yet.</div>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr><th>When</th><th>Who</th><th>What</th><th>Change</th></tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.audit_id}>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>
                      {fmtDateTime(e.created_at)}
                    </td>
                    <td>{e.user_name}</td>
                    <td>
                      {e.kind}
                      {e.label && <span className="mono dim"> {e.label}</span>}
                    </td>
                    <td className="dim">{describe(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {lineMenuFor && (() => {
        const it = lineMenuFor.item;
        const acts = lineActions(it);
        return (
          <>
            <div className="menu-scrim" onClick={() => setLineMenuFor(null)} />
            <div className="menu" role="menu"
                 style={{ position: 'fixed', top: lineMenuFor.top, bottom: lineMenuFor.bottom, right: lineMenuFor.right, left: 'auto' }}>
              {acts.edit && (
                <button role="menuitem" onClick={() => { setLineMenuFor(null); openLineEdit(it); }}>Edit line…</button>
              )}
              {acts.make && (
                <button role="menuitem" onClick={() => { setLineMenuFor(null); onOpenCard(it.id); }}>Make job card</button>
              )}
            </div>
          </>
        );
      })()}

      {addingLine && (
        <div className="backdrop">
          <div className="modal" style={{ width: 980, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto' }}
               onClick={(e) => e.stopPropagation()}>
            <NewOrderForm
              addTo={{ poId: order.id, customerId: order.customer_id, sales: `SO${order.so_number}`, company: order.customer_name }}
              onCancel={() => setAddingLine(false)}
              onCreated={(msg) => { setAddingLine(false); resetJobMakes(); setNotice(msg); void load(); }} />
          </div>
        </div>
      )}

      {editing !== null && (
        <div className="backdrop" onClick={() => !busy && setEditing(null)}>
          <div className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              {editing === 'order' ? (
                <>
                  <h3>Edit SO{order.so_number}</h3>
                  <p>{order.customer_name}</p>
                </>
              ) : (
                <>
                  <h3>Edit {editItem && lineRef(order.so_number, editItem.lineNo)} · {editItem?.productName}</h3>
                  <p>{order.customer_name}</p>
                </>
              )}
            </div>
            <div className="modal-body">
              {editing === 'order' ? (
                <>
                  <div className="field">
                    <label htmlFor="e-cust">Company</label>
                    <select id="e-cust" value={form.customerId ?? ''}
                            onChange={(e) => setForm({ ...form, customerId: e.target.value })}>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {form.customerId !== String(order.customer_id) && (
                      <div className="tag soon notice" style={{ marginTop: 8 }}>
                        Payments for this order move with it.
                      </div>
                    )}
                  </div>
                  <div className="row2">
                    <div className="field">
                      <label htmlFor="e-po">Customer PO</label>
                      <input id="e-po" type="text" value={form.poNumber ?? ''}
                             onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                             placeholder="Leave blank if none" />
                    </div>
                    <div className="field">
                      <label htmlFor="e-date">Order date</label>
                      <input id="e-date" type="date" value={form.poDate ?? ''}
                             onChange={(e) => setForm({ ...form, poDate: e.target.value })} />
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="e-pri">Priority</label>
                    <select id="e-pri" value={form.priority ?? 'NORMAL'}
                            onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                      <option value="LOW">Low</option>
                      <option value="NORMAL">Normal</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                <div className="field">
                  <label htmlFor="e-name" className="req">Box name</label>
                  <input id="e-name" type="text" value={form.productName ?? ''}
                         onChange={(e) => setForm({ ...form, productName: e.target.value })} />
                  {forcePrompt === 'rename'
                    || ((form.productName ?? '').trim().toLowerCase() !== originalName.toLowerCase()
                      && (form.productName ?? '').trim() !== '') ? (
                    <div className="subpanel" style={{ marginTop: 8 }}>
                      <div style={{ fontSize: '12.5px', marginBottom: 8 }}>
                        Name changed from <strong>{originalName}</strong> to{' '}
                        <strong>{(form.productName ?? '').trim()}</strong>
                      </div>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                                      marginBottom: 8, cursor: 'pointer' }}>
                        <input type="radio" name="rename" checked={renameMode === 'typo'}
                               onChange={() => setRenameMode('typo')}
                               style={{ marginTop: 3 }} />
                        <span>
                          <strong>It was a typo</strong>
                          <span className="dim"> — rename the saved box too</span>
                        </span>
                      </label>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                                      cursor: 'pointer' }}>
                        <input type="radio" name="rename" checked={renameMode === 'different'}
                               onChange={() => setRenameMode('different')}
                               style={{ marginTop: 3 }} />
                        <span>
                          <strong>It&rsquo;s a different box</strong>
                          <span className="dim"> — leave the saved box as it is</span>
                        </span>
                      </label>
                    </div>
                  ) : null}
                </div>
                <div className="row2" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  <div className="field">
                    <label htmlFor="e-make">Make</label>
                    <select id="e-make" value={form.make ?? ''}
                            onChange={(e) => setForm({
                              ...form, make: e.target.value,
                              layer1: faceChoosable(e.target.value) && !faceChoosable(form.make) ? '' : (form.layer1 ?? ''),
                            })}>
                      {BOX_TYPES.map((t) => <option key={t.key} value={t.make ?? ''}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="e-btype">Box type</label>
                    <input id="e-btype" type="text" value={form.boxType ?? ''}
                           onChange={(e) => setForm({ ...form, boxType: e.target.value })} />
                  </div>
                  <div className="field">
                    <label htmlFor="e-bsize">Box size</label>
                    <input id="e-bsize" type="text" value={form.boxSize ?? ''}
                           onChange={(e) => setForm({ ...form, boxSize: e.target.value })} />
                  </div>
                  <div className="field">
                    <label htmlFor="e-ups">Ups</label>
                    <input id="e-ups" type="number" min="1" value={form.ups ?? ''}
                           onChange={(e) => setForm({ ...form, ups: e.target.value })} />
                  </div>
                </div>
                {faceChoosable(form.make) && (
                  <div className="row2" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    <div className="field">
                      <label htmlFor="e-face" className="req">Layer 1</label>
                      <select id="e-face" value={form.layer1 ?? ''}
                              onChange={(e) => setForm({ ...form, layer1: e.target.value })}>
                        <option value="">Choose…</option>
                        <option value="BOARD">Board</option>
                        <option value="KRAFT">Kraft</option>
                      </select>
                    </div>
                  </div>
                )}
                <div className="row2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  <div className="field">
                    <label htmlFor="e-qty" className="req">Quantity</label>
                    <input id="e-qty" type="number" min="1" value={form.quantity ?? ''}
                           onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                  </div>
                  <div className="field">
                    <label htmlFor="e-del">Delivery date</label>
                    <input id="e-del" type="date" autoFocus value={form.deliveryDate ?? ''}
                           onChange={(e) => setForm({ ...form, deliveryDate: e.target.value })} />
                  </div>
                  <div className="field">
                    <label htmlFor="e-rate">Rate each (pre-GST)</label>
                    <input id="e-rate" type="number" min="0" step="0.01"
                           value={form.unitPrice ?? ''}
                           onChange={(e) => setForm({ ...form, unitPrice: e.target.value })} />
                  </div>
                </div>

                {forcePrompt === 'quantity'
                  || (Number(form.quantity) !== Number(originalQty)
                    && (form.quantity ?? '') !== ''
                    && impact.some((j) => j.status !== 'DRAFT')) ? (
                  <div className="subpanel" style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: '12.5px', marginBottom: 8 }}>
                      Quantity changed from <strong>{fmt(originalQty)}</strong> to{' '}
                      <strong>{fmt(form.quantity ?? '')}</strong>, and a card is
                      already released
                    </div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                                    marginBottom: 8, cursor: 'pointer' }}>
                      <input type="radio" name="qty" checked={quantityMode === 'typo'}
                             onChange={() => setQuantityMode('typo')}
                             style={{ marginTop: 3 }} />
                      <span>
                        <strong>The quantity was wrong</strong>
                        <span className="dim"> — correct the card, no new work</span>
                      </span>
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                                    cursor: 'pointer' }}>
                      <input type="radio" name="qty" checked={quantityMode === 'additional'}
                             onChange={() => setQuantityMode('additional')}
                             style={{ marginTop: 3 }} />
                      <span>
                        <strong>The order has changed</strong>
                        <span className="dim">
                          {' '}— keep the card, raise a new one for the difference
                        </span>
                      </span>
                    </label>
                  </div>
                ) : null}

                {editItem && Number(form.quantity) !== Number(editItem.quantity)
                  && editItem.jobs.some((x) => x.status !== 'CANCELLED') && (
                  <div className="subpanel" style={{ marginBottom: 12 }}>
                    <strong style={{ fontSize: '12.5px' }}>Jobs on this line</strong>
                    <ul className="undo-list" style={{ margin: '6px 0 0' }}>
                      {editItem.jobs.filter((x) => x.status !== 'CANCELLED').map((x) => (
                        <li key={x.id}><span className="mono">{x.jobNumber}</span> · {fmt(x.orderedQuantity)} · {x.place}</li>
                      ))}
                    </ul>
                  </div>
                )}
                </>
              )}
              <div className="field" style={{ marginBottom: needsReason || edits.length ? undefined : 0 }}>
                <label htmlFor="e-notes">Notes</label>
                <input id="e-notes" type="text" value={form.notes ?? ''}
                       onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              {needsReason && (
                <div className="field">
                  <label htmlFor="e-reason" className="req">Reason</label>
                  <input id="e-reason" type="text" value={form.reason ?? ''}
                         onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                </div>
              )}
              {edits.length > 0 && (
                <ul className="undo-list" style={{ margin: 0 }}>
                  {edits.map((e) => <li key={e.label}>{e.label} {e.from} → {e.to}</li>)}
                </ul>
              )}
              {editError && (
                <div className="tag late notice" style={{ marginTop: 12 }}>{editError}</div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn" disabled={busy}
                      onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn primary" disabled={busy || !canSave}
                      onClick={() => void saveEdit()}>
                {busy ? 'Saving\u2026' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {menuFor && (
        <>
          <div className="menu-scrim" onClick={() => setMenuFor(null)} />
          <div className="menu" role="menu"
               style={{ position: 'fixed', top: menuFor.top, bottom: menuFor.bottom, right: menuFor.right, left: 'auto' }}>
            <button role="menuitem" onClick={() => { const j = menuFor.job; setMenuFor(null); onOpenJob(j.id); }}>
              Open card
            </button>
            <div className="menu-sep" />
            <button role="menuitem"
                    onClick={() => { const j = menuFor.job; setMenuFor(null); setNotice(''); void openCancel(j); }}>
              Cancel job…
            </button>
          </div>
        </>
      )}

      {cancelling && (
        <div className="backdrop" onClick={() => !busy && setCancelling(null)}>
          <div className="modal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>Cancel {cancelling.jobNumber}?</h3>
              <p>
                {cancelling.status === 'DRAFT'
                  ? 'This card was never released, so nothing returns to pre-press.'
                  : `${fmt(cancelling.orderedQuantity)} goes back to pre-press to be re-carded.`}
              </p>
            </div>
            <div className="modal-body">
              {onOrder.map((o) => (
                <div key={o.name} className="tag waiting notice" role="alert"
                     style={{ marginBottom: 12, fontSize: 13, fontWeight: 500 }}>
                  {fmt(String(o.sheets))} sheets of {o.name} are on order for this job
                  {o.from ? ` from ${o.from}` : ''}{o.due ? `, due ${fmtDate(o.due)}` : ''}. If you cancel, they go
                  to stock when they arrive — close the order in Board › To order if the supplier can stop it.
                </div>
              ))}
              {returns.length > 0 && (
                <div className="field">
                  <label>Board coming back to the store</label>
                  {returns.map((r, i) => {
                    const bad = badReturn(r);
                    return (
                      <div key={r.itemId}
                           style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{r.name}</div>
                          <div className="dim" style={{ fontSize: 12 }}>Issued {fmt(String(r.issued))}</div>
                        </div>
                        <input type="number" min="0" max={r.issued} step="1"
                               aria-label={`Sheets of ${r.name} coming back`}
                               aria-invalid={bad}
                               style={{ width: 110, textAlign: 'right' }} value={r.sheets}
                               onChange={(e) => setReturns(returns.map((x, k) =>
                                 (k === i ? { ...x, sheets: e.target.value } : x)))} />
                      </div>
                    );
                  })}
                  <div className="hint">
                    {firstBadReturn
                      ? `Up to ${fmt(String(firstBadReturn.issued))} — what was issued — in whole sheets.`
                      : `${returnsNote} Anything not returned stays recorded against this job.`}
                  </div>
                </div>
              )}
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="cancel-reason" className="req">Reason</label>
                <input id="cancel-reason" type="text" autoFocus value={reason}
                       onChange={(e) => setReason(e.target.value)}
                       placeholder="e.g. entered under the wrong customer, duplicate order" />
                <div className="hint">
                  Cancelling cannot be undone — pre-press raises a new card against
                  the freed quantity instead. The reason is what explains the change
                  later.
                </div>
              </div>
              {cancelError && (
                <div className="tag late notice" style={{ marginTop: 12 }}>{cancelError}</div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn" disabled={busy}
                      onClick={() => setCancelling(null)}>Keep it</button>
              <button className="btn primary" disabled={busy || !reason.trim() || returnsInvalid}
                      onClick={() => void confirmCancel()}>
                {busy ? 'Cancelling…' : 'Cancel this job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Where one job's time went, stage by stage.
 *
 * The waits are the point. Listing only the stages makes a job look like eight
 * quick jobs in a row and hides the seven hours it stood beside a machine, so
 * the two are given equal rows and the same clock. The last row has no end
 * because it is what is happening now.
 */
function JobTiming({ job }: { job: Job }) {
  const total = (() => {
    const first = job.timeline[0];
    const last = job.timeline[job.timeline.length - 1];
    if (!first) return null;
    return last?.to ? fmtDuration(first.from, last.to) : fmtSoFar(first.from);
  })();

  return (
    <>
      <div className="hint" style={{ margin: '6px 0 8px' }}>
        Route written {fmtDateTime(job.timeline[0]?.from ?? null)}
        {total && <> · {total} from then to now</>}
      </div>
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th style={{ width: 44 }}>#</th>
              <th>Stage</th>
              <th>Start at</th>
              <th>End at</th>
              <th className="num">Duration</th>
            </tr>
          </thead>
          <tbody>
            {job.timeline.map((s, n) => {
              const running = s.to === null;
              return (
                <tr key={s.position} className={running ? 'soon' : ''}>
                  <td className="dim mono">{String(n + 1).padStart(2, '0')}</td>
                  <td>
                    <span className={`tag ${s.kind === 'WAIT' ? 'blocked' : 'high'}`}>
                      {s.label}
                    </span>
                  </td>
                  <td className="dim" style={{ whiteSpace: 'nowrap' }}>
                    {fmtDateTime(s.from)}
                  </td>
                  <td className="dim" style={{ whiteSpace: 'nowrap' }}>
                    {fmtDateTime(s.to)}
                  </td>
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    {running
                      ? <span className="dim">{fmtSoFar(s.from)}</span>
                      : <strong>{fmtDuration(s.from, s.to)}</strong>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>

  );
}

/**
 * Date and time in local terms. Timestamps arrive as ISO with a zone, so unlike
 * a bare date these are safe to hand to the Date constructor.
 */
function fmtDateTime(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

/**
 * How long something took, in the units a floor actually says out loud.
 *
 * Days and hours lead because they are the ones worth arguing about; seconds
 * are kept because a stage closed in four seconds is a real thing to notice --
 * it usually means somebody clicked Done without doing anything. Zero units in
 * the middle are dropped, so a long wait reads "7h 36m" rather than
 * "0d 7h 36m 14s" and the eye goes straight to the number that matters.
 */
function fmtDuration(fromISO: string, toISO: string | null): string {
  if (!toISO) return '—';
  const ms = new Date(toISO).getTime() - new Date(fromISO).getTime();
  // Clocks and hand-edited timestamps can put these out of order. Better to
  // show nothing than a negative duration nobody can act on.
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return 'instant';

  const s = Math.floor(ms / 1000);
  const parts: string[] = [];
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  // Seconds only when they are the whole story, or the only detail left.
  if (sec && !d && (!h || !m)) parts.push(`${sec}s`);
  return parts.join(' ') || 'instant';
}

/** Time from a start until now, for the step still running. */
function fmtSoFar(fromISO: string): string {
  const out = fmtDuration(fromISO, new Date().toISOString());
  return out === '—' ? '—' : `${out} so far`;
}
